import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  MagnifyingGlass, CaretRight, Sparkle, Star, ClockCounterClockwise, FloppyDisk,
  Copy, Trash, MagicWand, Sliders, BookmarkSimple, X, ArrowUUpLeft, ArrowUUpRight, CircleNotch, Check,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { promptApi } from "../useCreative";
import { PROMPT_GROUPS, renderStructured } from "../creativeUtils";

const CONFIDENCE = {
  high: "bg-success/15 text-success", medium: "bg-warning/15 text-warning", low: "bg-muted text-muted-foreground",
};
const WHOLE_ACTIONS = [
  ["improve", "Improve"], ["expand", "Expand"], ["simplify", "Simplify"], ["rewrite", "Rewrite"],
  ["make_more_cinematic", "Make More Cinematic"], ["make_more_realistic", "Make More Realistic"],
  ["make_more_commercial", "Make More Commercial"], ["make_more_viral", "Make More Viral"],
  ["optimize_for_provider", "Optimize For Provider"],
];
const FIELD_ACTION = {
  style: "suggest_style", camera: "suggest_camera", lighting: "suggest_lighting",
  composition: "suggest_composition", color_palette: "suggest_color_palette", mood: "suggest_mood",
};

// Reusable, provider-agnostic field. Exposes a prepared (disabled) AI-assist
// hook per field so Suggest/Improve/… can be wired later with NO redesign.
function PromptField({ field, value, onChange, kind, onAssist, aiBusy }) {
  const listId = `dl-${field.key}`;
  const common = {
    value: value ?? "",
    onChange: (e) => onChange(field.key, e.target.value),
    placeholder: field.placeholder || "",
    className: "w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ai",
    "data-testid": `prompt-field-${field.key}`,
  };
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="promo-overline">{field.label}</label>
        {field.ai?.length ? (
          <button type="button" title={`AI assist: ${field.ai.join(", ")}`} onClick={() => onAssist(field)}
            disabled={aiBusy}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-ai transition-colors hover:bg-ai/10 disabled:opacity-40"
            data-testid={`prompt-ai-${field.key}`}>
            {aiBusy ? <CircleNotch size={11} className="animate-spin" /> : <MagicWand size={11} />} AI
          </button>
        ) : null}
      </div>
      {field.type === "textarea" ? (
        <textarea rows={2} {...common} />
      ) : field.type === "select" ? (
        <select {...common} className={common.className + " capitalize"}>
          {(field.opts || []).map((o) => <option key={o} value={o}>{o === "" ? "Default" : o}</option>)}
        </select>
      ) : (
        <>
          <input type={field.type === "number" ? "number" : "text"} list={field.opts ? listId : undefined} {...common} />
          {field.opts && <datalist id={listId}>{field.opts.map((o) => <option key={o} value={o} />)}</datalist>}
        </>
      )}
    </div>
  );
}

function Group({ group, structured, set, kind, search, forceOpen, onAssist, aiBusyField }) {
  const [open, setOpen] = useState(group.defaultOpen || false);
  const fields = group.fields.filter((f) => (!f.kinds || f.kinds.includes(kind)) &&
    (!search || f.label.toLowerCase().includes(search) || f.key.includes(search)));
  if (group.kinds && !group.kinds.includes(kind)) return null;
  if (fields.length === 0) return null;
  const isOpen = forceOpen || open;
  const filled = fields.filter((f) => structured[f.key]).length;
  return (
    <div className="rounded-md border border-border" data-testid={`prompt-group-${group.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left transition-colors hover:bg-accent/40"
        data-testid={`prompt-group-toggle-${group.id}`}>
        <span className="flex items-center gap-2 text-sm font-medium">
          <CaretRight size={13} className={`transition-transform ${isOpen ? "rotate-90" : ""}`} />
          {group.label}
          {filled > 0 && <span className="rounded-full bg-ai/15 px-1.5 py-0.5 text-[10px] font-medium text-ai">{filled}</span>}
        </span>
      </button>
      {isOpen && (
        <div className="grid grid-cols-1 gap-3 border-t border-border p-3 sm:grid-cols-2">
          {fields.map((f) => <PromptField key={f.key} field={f} value={structured[f.key]} onChange={set} kind={kind} onAssist={onAssist} aiBusy={aiBusyField === f.key} />)}
        </div>
      )}
    </div>
  );
}

export function VisualPromptBuilder({ structured, onChange, kind = "image", onRenderedChange, targetProvider }) {
  const [search, setSearch] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [tab, setTab] = useState(null); // presets | favorites | history
  const [presets, setPresets] = useState([]);
  const [history, setHistory] = useState([]);
  const [aiMenu, setAiMenu] = useState(false);
  const [aiBusy, setAiBusy] = useState(null); // action or field key while loading
  const [suggestion, setSuggestion] = useState(null); // preview modal

  // Undo/redo over the structured prompt (suggestion chaining preserves history).
  const hist = useRef({ past: [], future: [] });
  const last = useRef(JSON.stringify(structured || {}));
  useEffect(() => { // external replacement (e.g. scene switch / preset) resets history
    const cur = JSON.stringify(structured || {});
    if (cur !== last.current) { hist.current = { past: [], future: [] }; last.current = cur; }
  }, [structured]);

  const commit = useCallback((next) => {
    hist.current.past.push(structured || {});
    hist.current.future = [];
    last.current = JSON.stringify(next || {});
    onChange(next);
  }, [structured, onChange]);

  const undo = () => {
    if (!hist.current.past.length) return;
    const prev = hist.current.past.pop();
    hist.current.future.push(structured || {});
    last.current = JSON.stringify(prev || {});
    onChange(prev);
  };
  const redo = () => {
    if (!hist.current.future.length) return;
    const nxt = hist.current.future.pop();
    hist.current.past.push(structured || {});
    last.current = JSON.stringify(nxt || {});
    onChange(nxt);
  };

  const rendered = useMemo(() => renderStructured(structured, kind), [structured, kind]);
  useEffect(() => { onRenderedChange?.(rendered); }, [rendered, onRenderedChange]);

  const set = (key, val) => commit({ ...structured, [key]: val === "" ? undefined : val });
  const setManual = (text) => commit({ ...structured, extra: { ...(structured.extra || {}), manual_prompt: text || undefined } });
  const manual = structured?.extra?.manual_prompt || "";

  const runAssist = async (action, field = null) => {
    setAiMenu(false);
    setAiBusy(field || action);
    try {
      const body = { structured, kind, action, field, target_provider: targetProvider || null };
      const res = await promptApi.assist(body);
      setSuggestion(res);
    } catch { toast.error("AI Assist failed"); }
    setAiBusy(null);
  };
  const assistField = (f) => runAssist(FIELD_ACTION[f.key] || "improve", FIELD_ACTION[f.key] ? null : f.key);
  const applySuggestion = () => { if (suggestion) { commit(suggestion.suggested); toast.success("Suggestion applied"); setSuggestion(null); } };

  const loadLibrary = useCallback(async () => {
    try {
      const [ps, hs] = await Promise.all([promptApi.listPresets(), promptApi.listHistory(30)]);
      setPresets(ps); setHistory(hs);
    } catch { /* non-blocking */ }
  }, []);
  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  const openTab = (t) => { setTab(tab === t ? null : t); if (tab !== t) loadLibrary(); };

  const savePreset = async () => {
    const name = window.prompt("Preset name");
    if (!name) return;
    try { await promptApi.createPreset({ name, structured, kind }); toast.success("Preset saved"); loadLibrary(); }
    catch { toast.error("Could not save preset"); }
  };
  const applyStructured = (sp) => { commit({ ...sp }); toast.success("Applied"); };
  const toggleFav = async (id) => { await promptApi.toggleFavorite(id); loadLibrary(); };
  const delPreset = async (id) => { await promptApi.deletePreset(id); loadLibrary(); toast.success("Preset deleted"); };

  const favorites = presets.filter((p) => p.is_favorite);
  const list = tab === "favorites" ? favorites : presets;
  const canUndo = hist.current.past.length > 0;
  const canRedo = hist.current.future.length > 0;

  return (
    <div className="space-y-4" data-testid="prompt-builder">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[140px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value.toLowerCase())} placeholder="Search fields…"
            className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-2 text-sm outline-none focus:border-ai"
            data-testid="prompt-search" />
        </div>
        <div className="relative">
          <button type="button" onClick={() => setAiMenu((m) => !m)} className={tabBtn(aiMenu) + " !border-ai/40 !text-ai"} data-testid="prompt-ai-assist">
            <MagicWand size={13} weight="fill" /> AI Assist
          </button>
          {aiMenu && (
            <div className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-border bg-card p-1 shadow-lg" data-testid="prompt-ai-menu">
              {WHOLE_ACTIONS.map(([a, label]) => (
                <button key={a} type="button" onClick={() => runAssist(a)} className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs hover:bg-accent" data-testid={`prompt-ai-action-${a}`}>
                  <Sparkle size={12} className="text-ai" /> {label}
                </button>
              ))}
              <button type="button" onClick={() => runAssist("translate")} className="flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs hover:bg-accent" data-testid="prompt-ai-action-translate">
                <Sparkle size={12} className="text-ai" /> Translate…
              </button>
            </div>
          )}
        </div>
        <button type="button" onClick={undo} disabled={!canUndo} className={tabBtn(false) + " disabled:opacity-40"} title="Undo" data-testid="prompt-undo"><ArrowUUpLeft size={13} /></button>
        <button type="button" onClick={redo} disabled={!canRedo} className={tabBtn(false) + " disabled:opacity-40"} title="Redo" data-testid="prompt-redo"><ArrowUUpRight size={13} /></button>
        <button type="button" onClick={() => openTab("presets")} className={tabBtn(tab === "presets")} data-testid="prompt-library-toggle"><BookmarkSimple size={13} /> Presets</button>
        <button type="button" onClick={() => openTab("favorites")} className={tabBtn(tab === "favorites")} data-testid="prompt-favorites-toggle"><Star size={13} /> Favorites</button>
        <button type="button" onClick={() => openTab("history")} className={tabBtn(tab === "history")} data-testid="prompt-history-toggle"><ClockCounterClockwise size={13} /> History</button>
        <button type="button" onClick={() => setAdvanced((a) => !a)} className={tabBtn(advanced)} data-testid="prompt-advanced-toggle"><Sliders size={13} /> Advanced</button>
      </div>

      {aiBusy && <div className="flex items-center gap-2 text-xs text-ai" data-testid="prompt-ai-loading"><CircleNotch size={13} className="animate-spin" /> AI is thinking…</div>}

      {/* Library drawer */}
      {tab && (
        <div className="rounded-md border border-border bg-muted/20 p-3" data-testid={`prompt-panel-${tab}`}>
          <div className="mb-2 flex items-center justify-between">
            <span className="promo-overline capitalize">{tab}</span>
            <div className="flex items-center gap-2">
              {tab !== "history" && (
                <button type="button" onClick={savePreset} className="flex items-center gap-1 text-xs text-brand hover:underline" data-testid="prompt-save-preset">
                  <FloppyDisk size={12} /> Save current
                </button>
              )}
              {tab === "history" && history.length > 0 && (
                <button type="button" onClick={async () => { await promptApi.clearHistory(); loadLibrary(); }} className="flex items-center gap-1 text-xs text-destructive hover:underline" data-testid="prompt-clear-history">
                  <Trash size={12} /> Clear
                </button>
              )}
              <button type="button" onClick={() => setTab(null)} className="text-muted-foreground hover:text-foreground"><X size={14} /></button>
            </div>
          </div>
          {tab === "history" ? (
            history.length === 0 ? <p className="text-xs text-muted-foreground">No history yet.</p> : (
              <ul className="space-y-1.5 max-h-56 overflow-auto">
                {history.map((h, i) => (
                  <li key={h.id}>
                    <button type="button" onClick={() => applyStructured(h.structured)} className="w-full truncate rounded px-2 py-1.5 text-left text-xs hover:bg-accent" data-testid={`prompt-history-item-${i}`} title={h.rendered}>
                      {h.rendered || "(empty)"}
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : list.length === 0 ? <p className="text-xs text-muted-foreground">No {tab} yet — build a prompt and “Save current”.</p> : (
            <ul className="space-y-1.5 max-h-56 overflow-auto">
              {list.map((p) => (
                <li key={p.id} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-accent" data-testid={`prompt-preset-${p.id}`}>
                  <button type="button" onClick={() => applyStructured(p.structured)} className="flex-1 truncate text-left text-xs" title={p.name}>{p.name}</button>
                  <button type="button" onClick={() => toggleFav(p.id)} title="Favorite" data-testid={`prompt-preset-fav-${p.id}`}>
                    <Star size={13} weight={p.is_favorite ? "fill" : "regular"} className={p.is_favorite ? "text-warning" : "text-muted-foreground"} />
                  </button>
                  <button type="button" onClick={() => delPreset(p.id)} title="Delete" className="text-muted-foreground hover:text-destructive" data-testid={`prompt-preset-del-${p.id}`}><Trash size={13} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Sections */}
      <div className="space-y-2">
        {PROMPT_GROUPS.map((g) => (
          <Group key={g.id} group={g} structured={structured} set={set} kind={kind} search={search} forceOpen={!!search} onAssist={assistField} aiBusyField={aiBusy} />
        ))}
      </div>

      {/* Live Preview + Advanced editor */}
      <div className="rounded-md border border-ai/30 bg-ai/5 p-3" data-testid="prompt-preview-wrap">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="promo-overline flex items-center gap-1.5 text-ai"><Sparkle size={12} weight="fill" /> Live Prompt Preview{manual ? " · manual override" : ""}</span>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(rendered); toast.success("Copied"); }} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="prompt-copy">
            <Copy size={12} /> Copy
          </button>
        </div>
        {advanced ? (
          <div className="space-y-1.5">
            <textarea rows={4} value={manual || rendered} onChange={(e) => setManual(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-ai"
              data-testid="prompt-manual-edit" placeholder="Edit the final prompt directly…" />
            {manual && (
              <button type="button" onClick={() => setManual("")} className="text-xs text-brand hover:underline" data-testid="prompt-reset-manual">
                Reset to structured
              </button>
            )}
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-sm leading-relaxed" data-testid="prompt-preview">
            {rendered || <span className="text-muted-foreground">Fill in the sections above to compose your prompt…</span>}
          </p>
        )}
      </div>

      {/* AI suggestion preview (preview-only until applied) */}
      {suggestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSuggestion(null)} data-testid="prompt-suggestion-modal">
          <div className="w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-semibold"><MagicWand size={16} className="text-ai" weight="fill" /> AI Suggestion</h3>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${CONFIDENCE[suggestion.confidence] || CONFIDENCE.medium}`} data-testid="prompt-suggestion-confidence">
                {suggestion.confidence} confidence{suggestion.fallback ? " · heuristic" : ""}
              </span>
            </div>
            <p className="mb-3 text-sm text-muted-foreground" data-testid="prompt-suggestion-explanation">{suggestion.explanation}</p>
            <div className="mb-3">
              <div className="promo-overline mb-1.5">Changed fields</div>
              <div className="space-y-1.5" data-testid="prompt-suggestion-changes">
                {Object.entries(suggestion.patch || {}).map(([k, v]) => (
                  <div key={k} className="text-xs">
                    <span className="font-medium capitalize">{k.replace(/_/g, " ")}:</span>{" "}
                    {structured[k] ? <span className="text-muted-foreground line-through">{String(structured[k])}</span> : null}{" "}
                    <span className="text-ai">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mb-4 rounded-md border border-ai/30 bg-ai/5 p-3">
              <div className="promo-overline mb-1">New rendered preview</div>
              <p className="whitespace-pre-wrap text-sm" data-testid="prompt-suggestion-preview">{suggestion.rendered_preview}</p>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSuggestion(null)} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent" data-testid="prompt-suggestion-discard">Discard</button>
              <button type="button" onClick={applySuggestion} className="flex items-center gap-1.5 rounded-md bg-ai px-3 py-1.5 text-sm font-medium text-ai-foreground hover:opacity-90" data-testid="prompt-suggestion-apply"><Check size={14} /> Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function tabBtn(active) {
  return `flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors ${active ? "border-ai bg-ai/10 text-ai font-medium" : "border-border text-muted-foreground hover:bg-accent hover:text-foreground"}`;
}
